import {useAxiosClient} from "~/use/axios/client";
import {
    AllmaJobApi,
    GmActionModelsModelsContractType,
    GmModelLibraryModelsAllmaJobsAllmaJobModel
} from "~/gen/openapi/sblService";
import {keepPreviousData, useQuery, UseQueryOptions} from "@tanstack/vue-query";
import {MS_DAY, MS_MINUTE} from "~/constants/time";
import {useOnline} from "@vueuse/core";
import type {JobImageOptions, ThematicMapType} from "~/models/allmajob";
import type {FullContract, MapImage} from "~/models/contract";
import {generateUUID} from "~/helpers/uuid";
import {base64ImageSize, imageCompressor, imageToBase64} from "~/helpers/util";
import {queryClient} from "~/plugins/vue-query";

export const useAllmaService = () => {
    const { axiosClient } = useAxiosClient()
    const allmaJobApi = new AllmaJobApi(undefined, '', axiosClient.value)
    const isOnline = useOnline()

    const defaultQueryOptions = {
        staleTime: 5 * MS_MINUTE,
        refetchOnWindowFocus: false,
        placeholderData: keepPreviousData,
        gcTime: MS_DAY,
        networkMode: 'offlineFirst',
        enabled: isOnline,
    } as UseQueryOptions<any>

    const getAllmaJobsByPropertyQueryOptions = (propertyId?: string, contractTypeFilter?: number): UseQueryOptions<Array<GmModelLibraryModelsAllmaJobsAllmaJobModel>> => {
        return {
            queryKey: ['allmaJobGetAllmaJobsByProperty', propertyId, contractTypeFilter],
            queryFn: async () => {
                try {
                    if (propertyId) {
                        return (await allmaJobApi.allmaJobGetAllmaJobsByProperty(propertyId, contractTypeFilter as unknown as GmActionModelsModelsContractType))?.data as Array<GmModelLibraryModelsAllmaJobsAllmaJobModel>
                    }
                    return []
                } catch (error) {
                    console.error(error)
                    return []
                }
            },
            ...defaultQueryOptions
        }
    }

    const allmaJobsByPropertyQuery = (propertyId?: string, contractTypeFilter?: number) => {
        const options = getAllmaJobsByPropertyQueryOptions(propertyId, contractTypeFilter)
        return useQuery({
            ...options
        })
    }

    const getOwnAllmaJobsQueryOptions = (contractTypeFilter?: number): UseQueryOptions<Array<GmModelLibraryModelsAllmaJobsAllmaJobModel>> => {
        return {
            queryKey: ['allmaJobGetOwnAllmaJobs', contractTypeFilter],
            queryFn: async () => {
                try {
                    return (await allmaJobApi.allmaJobGetOwnAllmaJobs(contractTypeFilter as unknown as GmActionModelsModelsContractType))?.data as Array<GmModelLibraryModelsAllmaJobsAllmaJobModel>
                } catch (error) {
                    console.error(error)
                    return []
                }
            },
            ...defaultQueryOptions
        }
    }

    const ownAllmaJobsQuery = (contractTypeFilter?: number) => {
        const options = getOwnAllmaJobsQueryOptions(contractTypeFilter)
        return useQuery({
            ...options
        })
    }

    const getAllUnusedAllmaJobsQueryOptions = (contractTypeFilter?: number): UseQueryOptions<Array<GmModelLibraryModelsAllmaJobsAllmaJobModel>> => {
        return {
            queryKey: ['allmaJobGetAllUnusedAllmaJobs', contractTypeFilter],
            queryFn: async () => {
                try {
                    return (await allmaJobApi.allmaJobGetAllUnusedAllmaJobs(contractTypeFilter as unknown as GmActionModelsModelsContractType))?.data as Array<GmModelLibraryModelsAllmaJobsAllmaJobModel>
                } catch (error) {
                    console.error(error)
                    return []
                }
            },
            ...defaultQueryOptions
        }
    }

    const allUnusedAllmaJobsQuery = (contractTypeFilter?: number, enable = ref(true)) => {
        const options = getAllUnusedAllmaJobsQueryOptions(contractTypeFilter)
        return useQuery({
            ...options,
            enabled: enable
        })
    }

    const getAllmaJobQueryOptions = (id?: string, update = false): UseQueryOptions<GmModelLibraryModelsAllmaJobsAllmaJobModel | undefined> => {
        return {
            queryKey: ['allmaJobGetAllmaJob', id],
            queryFn: async () => {
                try {
                    if (id) {
                        return (await allmaJobApi.allmaJobGetAllmaJob(id, update))?.data as GmModelLibraryModelsAllmaJobsAllmaJobModel
                    }
                } catch (error) {
                    console.error(error)
                    return Promise.reject(error)
                }
            },
            ...defaultQueryOptions,
        }
    }

    const allmaJobQuery = (id?: string, update = false) => {
        const options = getAllmaJobQueryOptions(id, update)
        return useQuery({
            ...options
        })
    }

    const getThematicMapTypesQueryOptions = (): UseQueryOptions<Array<ThematicMapType>> => {
        return {
            queryKey: ['allmaJobListThematicMapTypes'],
            queryFn: async () => {
                try {
                    return (await allmaJobApi.allmaJobListThematicMapTypes())?.data as Array<ThematicMapType>
                } catch (error) {
                    console.error(error)
                    return []
                }
            },
            ...defaultQueryOptions,
            staleTime: MS_DAY,
            gcTime: Infinity,
        }
    }

    const thematicMapTypesQuery = () => {
        const options = getThematicMapTypesQueryOptions()
        return useQuery({
            ...options
        })
    }

    const getAllmaJobImageQueryOptions = (options: JobImageOptions) : UseQueryOptions<string> => {
        return {
            queryKey: ['allmaJobGetImage', options.id, options.thematicMapId, options.withBuffer, options.zoomToBuffer, options.scale],
            queryFn: async () => {
                //@ts-ignore
                return (await allmaJobApi.allmaJobGetImage(options))?.data
            },
            ...defaultQueryOptions
        }
    }

    const fetchImages = async ({
                                   id,
                                   thematicMapId,
                                   withBuffer = true,
                                   zoomToBuffer = true,
                                   scale = 0
                               }: JobImageOptions): Promise<string> => {
        try {
            const options: JobImageOptions = {
                id,
                thematicMapId,
                withBuffer,
                zoomToBuffer,
                scale
            }

            const queryOptions = getAllmaJobImageQueryOptions(options)
            const jobImage = await queryClient.fetchQuery(queryOptions)
            try {
                const file = await fetch(`data:${jobImage}`) // Base64 image to file
                const compressedFile = await imageCompressor(await file.blob(), 0.7) // Adjust second number up (max 1) for higher quality, or down (min 0.2) for lower
                return await imageToBase64(compressedFile)
            } catch (error) {
                console.log(error)
                //@ts-ignore
                throw new Error(error)
            }
        } catch (error) {
            console.log(error)
            //@ts-ignore
            throw new Error(error)
        }
    }

    const addCurrentContractMapImagesToContract = (contract: WritableComputedRef<FullContract, FullContract>, image: MapImage) => {
        const existing = contract.value.MapImages?.find(
            (i: MapImage) => i.Name === image.Name
        )
        if (!existing) {
            contract.value.MapImages?.push(image)
        } else {
            delete existing.Deleted
        }
    }

    const addMapImages = async (contract: WritableComputedRef<FullContract, FullContract>, options: JobImageOptions): Promise<void> => {
        try {
            const image = await fetchImages(options)
            addCurrentContractMapImagesToContract(contract,{
                Id: 'temp_' + generateUUID(),
                Name: 'import_oppdrag_' + options.id.toString() + '_' + options.thematicMapId + '.jpg',
                Size: base64ImageSize(image),
                Data: image
            })

        } catch (error) {
            console.error(`could not fetch default job image from allma. JobId ${options.id}`)
        }
    }

    return {
        getAllmaJobsByPropertyQueryOptions,
        allmaJobsByPropertyQuery,
        getOwnAllmaJobsQueryOptions,
        ownAllmaJobsQuery,
        getAllUnusedAllmaJobsQueryOptions,
        allUnusedAllmaJobsQuery,
        getAllmaJobQueryOptions,
        allmaJobQuery,
        getThematicMapTypesQueryOptions,
        thematicMapTypesQuery,
        addMapImages,
    }
}